import React, { CSSProperties, SyntheticEvent, useEffect, useMemo, useState } from "react";

import { Checkbox, Form, FormField, FormGroup, Icon, Input } from "semantic-ui-react";

import { ComponentPropsGetterR } from "react-table-6";

// Serializer
import WorkShiftTemplateSerializer from "issara-sdk/types/WorkShiftTemplateSerializer_apps_HRM";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import PaginationShorthand from "react-lib/appcon/common/PaginationShorthand";

// Components
import SetupWorkShiftsTemplatePanel from "./SetupWorkShiftsTemplatePanel";
import TableSetupWorkShiftsTemplate from "./TableSetupWorkShiftsTemplate";

// Interface
import {
  BTN_ACTS,
  CARD_NAME,
  DEFAULT_LIMIT,
  PickedProps,
  RunSequence,
  SetProp,
  SetupWorkShiftsTemplateType,
} from "./sequence/WorkSchedule";

// Utils
import { calculateTotalWorkingTime, formatTimeHideSeconds } from "./HRMInterface";

// Type
type CardSetupWorkShiftsTemplateProps = {
  setProp: SetProp;
  // seq
  runSequence: RunSequence;
  setupWorkShiftsTemplate?: SetupWorkShiftsTemplateType;
} & PickedProps;

type Styles = Record<"center", CSSProperties>;

const styles: Styles = {
  center: { display: "flex", justifyContent: "center" },
};

const defaultFormState: Partial<WorkShiftTemplateSerializer> = {
  is_break_time: false,
  active: true,
  color_code: "#bfeafb",
};

const CardSetupWorkShiftsTemplate = (props: CardSetupWorkShiftsTemplateProps) => {
  const [formState, setFormState] =
    useState<Partial<WorkShiftTemplateSerializer>>(defaultFormState);
  const [totalWorking, setTotalWorking] = useState<{ hours: number; minutes: number }>({
    hours: 0,
    minutes: 0,
  });

  // Clear After Save Success
  useEffect(() => {
    if (props.successMessage?.[CARD_NAME]) {
      setFormState(defaultFormState);
    }
  }, [props.successMessage]);

  useEffect(
    () => () => {
      props.setProp("WorkScheduleSequence.setupWorkShiftsTemplate.filter", {});
    },
    []
  );

  useEffect(() => {
    const { start_time, end_time, is_break_time, start_break_time, end_break_time } = formState;

    const result = calculateTotalWorkingTime({
      startTime: start_time,
      endTime: end_time,
      isBreakTime: is_break_time,
      startBreakTime: start_break_time,
      endBreakTime: end_break_time,
    });

    setTotalWorking(result);
  }, [
    formState.start_time,
    formState.end_time,
    formState.is_break_time,
    formState.start_break_time,
    formState.end_break_time,
  ]);

  const filteredItems = useMemo(
    () => props.setupWorkShiftsTemplate?.pagination?.items ?? [],
    [props.setupWorkShiftsTemplate]
  );

  const formattedItems = useMemo(
    () =>
      filteredItems.map((item) => {
        return {
          ...item,
          code: <div id={`CardSetupWorkShiftsTemplate-Table-Code-${item.id}`} style={styles.center}>{item.code}</div>,
          name: <div style={styles.center}>{item.name}</div>,
          start_time: <div style={styles.center}>{formatTimeHideSeconds(item.start_time)}</div>,
          end_time: <div style={styles.center}>{formatTimeHideSeconds(item.end_time)}</div>,
          break_time: (
            <div style={styles.center}>
              {item.is_break_time && (
                <label>
                  {`${formatTimeHideSeconds(item.start_break_time)} - ${formatTimeHideSeconds(
                    item.end_break_time
                  )}`}
                </label>
              )}
            </div>
          ),
          total_working: (
            <div style={styles.center}>
              {`${item.total_working?.hours} hr ${item.total_working?.minutes} min`}
            </div>
          ),
          active: (
            <div style={styles.center}>
              {!item.active ? (
                <Icon color="red" name="remove circle" />
              ) : (
                <Icon color="green" name="check circle" />
              )}
            </div>
          ),
        };
      }),
    [filteredItems]
  );

  const handleSaveShiftsTemplate = () => {
    props.runSequence({
      sequence: "WorkSchedule",
      menu: "SetupShiftsTemplate",
      action: "SAVE",
      saveTemplate: formState,
    });
  };

  const handleEditShiftsTemplate = () => {
    if (!formState.id) {
      // for debug
      console.warn("Invalid Template ID To Edit!");
      return;
    }

    props.runSequence({
      sequence: "WorkSchedule",
      menu: "SetupShiftsTemplate",
      action: "EDIT",
      editTemplate: formState,
      templateId: formState.id,
    });
  };

  const handleDeleteShiftsTemplate = () => {
    if (!formState.id) {
      // for debug
      console.warn("Invalid Template ID To Delete!");
      return;
    }

    props.runSequence({
      sequence: "WorkSchedule",
      menu: "SetupShiftsTemplate",
      action: "DELETE",
      templateId: formState.id,
    });
  };

  const handleClearShiftsTemplate = () => {
    setFormState(defaultFormState);
  };

  const handleChangeData = (e: SyntheticEvent | null, data: Record<string, any>) => {
    const { name, checked, value, type } = data;

    const newValue = type === "checkbox" ? checked : value;

    let newFormState = {
      ...formState,
      [name]: newValue,
    };

    if (name === "is_break_time" && !newValue) {
      newFormState = {
        ...newFormState,
        start_break_time: undefined,
        end_break_time: undefined,
      };
    }

    setFormState(newFormState);
  };

  const handleSelectItem = (item: Partial<WorkShiftTemplateSerializer>) => {
    setFormState({
      ...item,
      is_break_time: !!item.is_break_time,
      active: !!item.active,
      color_code: item.color_code || defaultFormState.color_code,
    });
  };

  const handleGetTrProps: ComponentPropsGetterR = (state, rowInfo) => {
    if (!rowInfo) return {};

    const originalId = rowInfo?.original?.id;
    const isSelected = originalId === formState.id;

    return {
      style: {
        backgroundColor: isSelected ? "#e0f7fa" : "",
      },
      onClick: () => {
        const items = filteredItems || [];
        const foundItem = items.find((item) => item.id === originalId);

        if (foundItem) {
          handleSelectItem(foundItem);
        }
      },
    };
  };

  const handlePageChange = (activePage: number) => {
    props.runSequence({
      sequence: "WorkSchedule",
      action: "FETCH",
      activePage,
      menu: "SetupShiftsTemplate",
    });
  };

  const handleSearch = () => {
    props.runSequence({
      sequence: "WorkSchedule",
      action: "FETCH",
      activePage: props.setupWorkShiftsTemplate?.pagination?.activePage,
      menu: "SetupShiftsTemplate",
    });
  };

  const handleChangeFilter = (e: SyntheticEvent | null, data: Record<string, any>) => {
    const name = data.name;
    const value = data.type === "checkbox" ? data.checked : data.value;

    if (!name) {
      return;
    }

    props.setProp("WorkScheduleSequence.setupWorkShiftsTemplate.filter", {
      ...props.setupWorkShiftsTemplate?.filter,
      [name]: value,
    });
  };

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "grid",
          position: "relative",
          gridTemplateRows: "repeat(1, 1fr)",
          gridTemplateColumns: "repeat(3, 1fr)",
        }}
      >
        <div style={{ gridRow: "1/2", gridColumn: "1/2" }}>
          <SetupWorkShiftsTemplatePanel
            setProp={props.setProp}
            // func
            onSave={handleSaveShiftsTemplate}
            onEdit={handleEditShiftsTemplate}
            onDelete={handleDeleteShiftsTemplate}
            onClear={handleClearShiftsTemplate}
            onChange={handleChangeData}
            // data
            data={formState}
            totalWorking={totalWorking}
            // config
            paramKey="CardWordSchedule_SetupShiftsTemplate"
          />
        </div>
        <div style={{ gridRow: "1/2", gridColumn: "2/4", height: "100%", overflow: "auto" }}>
          <Form
            style={{ paddingLeft: "1rem", paddingRight: "1rem", paddingTop: "1rem" }}
            size="small"
          >
            <FormGroup>
              <FormField>
                <label style={{ fontSize: "18px", fontWeight: "bold" }}>Work Shifts List</label>
              </FormField>
            </FormGroup>
            <FormGroup inline>
              <FormField width={6} inline>
                <label style={{ fontSize: "1em", minWidth: "max-content" }}>Shift code/ Shift name</label>
                <Input
                  id="CardSetupWorkShiftsTemplate-Input-ShiftCode"
                  icon="search"
                  name="code_name"
                  value={props.setupWorkShiftsTemplate?.filter?.code_name ?? ""}
                  fluid
                  onChange={handleChangeFilter}
                />
              </FormField>
              <FormField>
                <ButtonLoadCheck
                  id="CardSetupWorkShiftsTemplate-ButtonLoadCheck-Search"
                  setProp={props.setProp}
                  color="blue"
                  paramKey={BTN_ACTS.SetupShiftsTemplate.FETCH}
                  size="small"
                  title="SEARCH"
                  fluid
                  buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SetupShiftsTemplate.FETCH]}
                  onClick={handleSearch}
                />
              </FormField>
              <FormField style={{ flex: 1 }} />
              <FormField style={{ marginTop: "auto" }} inline>
                <Checkbox
                  id="CardSetupWorkShiftsTemplate-Checkbox-IncludedInactivate"
                  checked={props.setupWorkShiftsTemplate?.filter?.exclude_inactive ?? false}
                  label="Included Inactive"
                  name="exclude_inactive"
                  onChange={handleChangeFilter}
                />
              </FormField>
            </FormGroup>
          </Form>
          <TableSetupWorkShiftsTemplate
            // data
            tableData={formattedItems}
            // func
            onGetTrProps={handleGetTrProps}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", margin: "1rem 1rem 0 0" }}>
            <PaginationShorthand
              activePage={props.setupWorkShiftsTemplate?.pagination?.activePage ?? 1}
              limit={DEFAULT_LIMIT}
              total={props.setupWorkShiftsTemplate?.pagination?.total}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardSetupWorkShiftsTemplate;
